import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IDealChangeRecord, AuditLogDealInformation, IDetailedAuditLog } from '@makemydeal/dr-platform-types';
import * as dealAuditLogSelectors from '@makemydeal/dr-dash-store/selectors/dealAuditLogSelectors';
import * as dealAuditLogActionCreators from '@makemydeal/dr-dash-store/actions/dealAuditLogActionCreators';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import type { BannerType } from '../types/AuditLog';
import { BANNER_WARNING_DELAY } from '../types/AuditLog';

export const useAuditLog = () => {
    const [auditLogSlideout, setAuditLogSlideout] = useState<IDealChangeRecord | null>(null);
    const [bannerType, setBannerType] = useState<BannerType | null>(null);
    const dispatch = useDispatch();

    // Selectors
    const isLoadingDealVersion = useSelector(dealAuditLogSelectors.getDealVersionLoadingStatus);
    const isPrintError = useSelector(dealAuditLogSelectors.getPrintErrorStatus);
    const isLoadingChangeLog = useSelector(dealAuditLogSelectors.getChangeLogLoadingStatus);
    const dealVersions = (useSelector(dealAuditLogSelectors.getTransformedDealVersions) || []) as AuditLogDealInformation[];
    const dealChangeLog: IDetailedAuditLog[] = useSelector(dealAuditLogSelectors.getDealChangeLog) || [];

    // Handler for setting slideout props
    const handleSetSlideoutProps = useCallback(
        (selectedItem: IDealChangeRecord) => {
            setAuditLogSlideout(selectedItem);
            dispatch(baseDealActionCreators.setAuditLogSlideoutStatus(true));
        },
        [dispatch]
    );

    // Fetch deal change log on mount
    useEffect(() => {
        dispatch(dealAuditLogActionCreators.fetchDealChangeLog());
    }, [dispatch]);

    // Timer logic for warning and success banners
    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (isLoadingChangeLog) {
            setBannerType(null); // Hide banner initially
            timer = setTimeout(() => {
                setBannerType('warning'); // Show warning after delay
            }, BANNER_WARNING_DELAY);
        } else {
            setBannerType('success'); // Show success when loading finishes
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isLoadingChangeLog]);

    return {
        auditLogSlideout,
        bannerType,
        isLoadingDealVersion,
        isPrintError,
        isLoadingChangeLog,
        dealVersions,
        dealChangeLog,
        handleSetSlideoutProps
    };
};
